import { Injectable } from "@angular/core";
import { DeviceContext } from "../context/deviceContext";
import { MouseEventService } from "./mouseEventService";
import { ElementRect } from "../mcrc-api";

@Injectable({
    providedIn: 'root'
})
export class ScreenBoundsService {
    constructor() { }
    private computeScreenBounds(ctx: DeviceContext): ElementRect {
        let screenWidth = ctx.skinScreenWidth * ctx.scale;
        let screenHeight = ctx.skinScreenHeight * ctx.scale;
        let rotatedCorrectionDeltaX: number, rotatedCorrectionDeltaY: number;
        const fullRotation = (ctx.screen.rotation + ctx.targetRotation + ctx.clientSideRotation) & 3;
        const skinScreenRotation = (ctx.screen.rotation + ctx.targetRotation) & 3;
        const offsetX = ctx.screen.left;
        const offsetXBottom = ctx.screen.dw - ctx.screen.right;
        const offsetY = ctx.screen.top;
        const offsetYBottom = ctx.screen.dh - ctx.screen.bottom;
        if (ctx.imageCapture) {
            let rotated = ctx.imageCapture.isFullScreen && ((ctx.skinScreenWidth > ctx.skinScreenHeight && ctx.imageCapture.width < ctx.imageCapture.height) ||
                (ctx.skinScreenWidth < ctx.skinScreenHeight && ctx.imageCapture.width > ctx.imageCapture.height));
            if (rotated || ((skinScreenRotation == 1 || skinScreenRotation == 3) && ctx.deviceSupportsScreenProjection)) {
                // we perform a swap before the regular computation
                let temp = screenHeight;
                screenHeight = screenWidth;
                screenWidth = temp;
            }
        }

        let temp: number;
        switch (fullRotation) {
            case 0:
                rotatedCorrectionDeltaX = offsetX * ctx.scale;
                rotatedCorrectionDeltaY = offsetY * ctx.scale;
                break;
            case 1:
                temp = screenHeight;
                screenHeight = screenWidth;
                screenWidth = temp;
                rotatedCorrectionDeltaX = offsetY * ctx.scale;
                rotatedCorrectionDeltaY = offsetXBottom * ctx.scale;
                break;
            case 2:
                rotatedCorrectionDeltaX = offsetXBottom * ctx.scale;
                rotatedCorrectionDeltaY = offsetYBottom * ctx.scale;
                break;
            case 3:
                temp = screenHeight;
                screenHeight = screenWidth;
                screenWidth = temp;
                rotatedCorrectionDeltaX = offsetYBottom * ctx.scale;
                rotatedCorrectionDeltaY = offsetX * ctx.scale;
                break;
        }

        const screenXOffset = ctx.left + rotatedCorrectionDeltaX;
        const screenYOffset = ctx.top + rotatedCorrectionDeltaY;

        return <ElementRect>{ x: screenXOffset, y: screenYOffset, width: screenWidth, height: screenHeight };
    }

    public isWithinScreenBounds(context: DeviceContext, event: any, isIrregularSkin: boolean): boolean {
        const screenBounds = this.computeScreenBounds(context);
        const mouseX = (event.offsetX || (event.pageX - event.currentTarget.offsetLeft)) * context.scaleFactor;
        const mouseY = (event.offsetY || (event.pageY - event.currentTarget.offsetTop)) * context.scaleFactor;

        // Check if the click is within the scaled skin rectangle...
        const isWithinScreenRectangle = (mouseX >= screenBounds.x &&
            mouseX < screenBounds.x + screenBounds.width &&
            mouseY >= screenBounds.y &&
            mouseY < screenBounds.y + screenBounds.height
        );

        if (!isWithinScreenRectangle || !isIrregularSkin) {
            // Click is either outside the screen rectangle, so we don't need to check for anything else
            // Or the screen is NOT irregular (so we have enough with checking if it is within the rectangle)
            return isWithinScreenRectangle;
        }

        // The skin screen is irregular
        // For irregular screens fully or partially transparent skin pixels are considered screen area
        return this.checkIfMouseIsOverTransparentSkinPixel(context, event);
    }

    private checkIfMouseIsOverTransparentSkinPixel(context: DeviceContext, event: any): boolean {
        MouseEventService.fixEvent(event);

        // extract the screen pixel
        const mouseX = event.offsetX || (event.pageX - event.currentTarget.offsetLeft);
        const mouseY = event.offsetY || (event.pageY - event.currentTarget.offsetTop);
        const scaledSkinX = Math.ceil(mouseX - context.left);
        const scaledSkinY = Math.ceil(mouseY - context.top);

        let tmpCanvasContext = context.skinCanvas.getContext("2d");
        let pixelData = tmpCanvasContext.getImageData(scaledSkinX, scaledSkinY, 1, 1).data;

        return pixelData[3] < 255; // non screen area pixels within skin are fully opaque including notches/cameras
    }
}
